<script setup lang="ts">
import { computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import QuickAdd from '../components/QuickAdd.vue'
import SearchPalette from '../components/SearchPalette.vue'
import { hasHistoryBack, isInstalled, parentOf } from '../lib/back'
import { compactDate, initials } from '../lib/format'
import { useIdentity } from '../lib/identity'
import { clearSavedCopies, useOffline } from '../lib/offline'
import { shortcutLabel } from '../lib/palette'

const { identity, error, loading, offline } = useIdentity()
// The page's own reads start beside /api/me, not after it: the page shows
// while it is asked, and only a failed answer (signed out — which leaves for
// the sign-in page —, offline with nothing saved) takes it away.
const ready = computed(() => identity.value !== null || loading.value)
const { savedAt } = useOffline()

const route = useRoute()
const router = useRouter()
// Only the installed app: a browser tab has its own back button.
const installed = isInstalled()
// vue-router writes history.state before it updates the route, so reading
// it here, keyed on the route, sees the entry just arrived at.
const canGoBack = computed(() => {
  void route.fullPath
  return installed && (hasHistoryBack(history.state) || !!parentOf(route))
})

function goBack() {
  if (hasHistoryBack(history.state)) router.back()
  else {
    const up = parentOf(route)
    if (up) void router.replace(up)
  }
}

/** The ⌘K palette (SearchPalette.vue), from the header's button. */
const searchKey = shortcutLabel(navigator.platform || navigator.userAgent)
const openSearch = () => window.dispatchEvent(new CustomEvent('memory:search'))

// The saved copies go before the session does, so nothing private outlives
// sign-out on this device even if the logout request never arrives.
async function signOut(e: Event) {
  const form = e.target as HTMLFormElement
  await clearSavedCopies().catch(() => {})
  form.submit()
}
</script>

<template>
  <div class="shell">
    <header class="shell-header">
      <button
        v-if="canGoBack"
        type="button"
        class="btn ghost icon back"
        aria-label="Back"
        title="Back"
        @click="goBack"
      >
        <svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true">
          <path
            d="M10 3.5 5.5 8l4.5 4.5"
            fill="none"
            stroke="currentColor"
            stroke-width="1.7"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </button>
      <RouterLink to="/" class="brand">
        <svg viewBox="0 0 20 20" width="20" height="20" aria-hidden="true">
          <rect width="20" height="20" rx="5" fill="var(--accent)" />
          <path
            d="M5 14V6.5l5 4.5 5-4.5V14"
            fill="none"
            stroke="var(--on-accent)"
            stroke-width="1.8"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
        <span class="brand-name">Memory</span>
      </RouterLink>
      <nav class="nav">
        <RouterLink to="/" :class="{ on: $route.name === 'home' || $route.name === 'link' }"
          >Library</RouterLink
        >
        <RouterLink to="/calendar" :class="{ on: $route.path.startsWith('/calendar') }"
          >Calendar</RouterLink
        >
        <RouterLink to="/ledger" :class="{ on: $route.path.startsWith('/ledger') }"
          >Ledger</RouterLink
        >
        <RouterLink to="/people" :class="{ on: $route.path.startsWith('/people') }"
          >People</RouterLink
        >
        <RouterLink to="/sync" :class="{ on: $route.name === 'sync' }">Sync</RouterLink>
        <RouterLink to="/devices" :class="{ on: $route.name === 'devices' }">Devices</RouterLink>
      </nav>
      <div v-if="identity" class="search-slot">
        <button
          type="button"
          class="search-button"
          :title="`Search everything (${searchKey})`"
          aria-label="Search"
          @click="openSearch"
        >
          <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true">
            <circle cx="7" cy="7" r="4.5" fill="none" stroke="currentColor" stroke-width="1.6" />
            <path
              d="M10.5 10.5 14 14"
              stroke="currentColor"
              stroke-width="1.6"
              stroke-linecap="round"
            />
          </svg>
          <span class="search-label">Search</span>
          <span class="kbd search-kbd">{{ searchKey }}</span>
        </button>
      </div>
      <div class="identity">
        <span v-if="loading" class="muted">…</span>
        <template v-else-if="identity">
          <span class="avatar" :title="identity.name ?? identity.login">{{
            initials(identity.name ?? identity.login)
          }}</span>
          <span class="login">{{ identity.login }}</span>
          <form method="post" action="/auth/logout" @submit.prevent="signOut">
            <button type="submit" class="btn ghost sign-out" aria-label="Sign out" title="Sign out">
              <svg viewBox="0 0 20 20" width="16" height="16" aria-hidden="true">
                <path
                  d="M8 4H5a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h3M12 6.5 15.5 10 12 13.5M15.5 10H8"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.6"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
              <span class="sign-out-label">Sign out</span>
            </button>
          </form>
        </template>
        <span v-else class="identity-error" :title="error ?? ''">{{
          offline ? 'offline' : 'not signed in'
        }}</span>
      </div>
    </header>

    <p v-if="savedAt" class="offline" role="status">
      Offline — showing copies saved {{ compactDate(savedAt) }}
    </p>

    <main class="shell-main scroller">
      <p v-if="error" class="banner">{{ error }}</p>
      <RouterView v-if="ready" />
    </main>
    <QuickAdd v-if="identity" />
    <SearchPalette v-if="identity" />
  </div>
</template>

<style scoped>
.shell {
  display: flex;
  flex-direction: column;
  height: 100vh;
  height: 100dvh;
  /* viewport-fit=cover: keep content off the home indicator and, in
     landscape, the notch. The top inset is 0 under iOS's default status bar
     style, but is honoured in case that ever changes. */
  padding: env(safe-area-inset-top) env(safe-area-inset-right) env(safe-area-inset-bottom)
    env(safe-area-inset-left);
}

.shell-header {
  display: flex;
  align-items: center;
  gap: 1.5rem;
  flex-shrink: 0;
  height: var(--header-h);
  padding: 0 1rem;
  background: var(--surface);
  border-bottom: 1px solid var(--line);
}

/* Sits tight against the brand: the header's gap is for the nav. */
.back {
  flex-shrink: 0;
  margin-right: -1rem;
  margin-left: -0.35rem;
}

.brand {
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  gap: 0.5rem;
  font-weight: 650;
  font-size: 15px;
  color: var(--fg);
  letter-spacing: -0.01em;
}

.brand:hover {
  text-decoration: none;
}

/* The nav gives way before the page does: if the header still runs out of
   room, the links scroll inside it instead of widening the page, which a
   phone would then let you drag sideways. */
.nav {
  display: flex;
  gap: 0.15rem;
  min-width: 0;
  overflow-x: auto;
  scrollbar-width: none;
}

.nav::-webkit-scrollbar {
  display: none;
}

.nav a {
  flex-shrink: 0;
}

.nav a {
  padding: 0.3rem 0.65rem;
  font-size: 13.5px;
  font-weight: 500;
  color: var(--muted);
  border-radius: var(--radius-sm);
}

.nav a:hover {
  color: var(--fg);
  background: var(--hover);
  text-decoration: none;
}

.nav a.on {
  color: var(--fg);
  background: var(--surface-2);
}

/* The space between the nav and the identity, with the search field centred
   in it: it grows with the window up to a field's width, and keeps clear of
   both neighbours. Its basis is 0, so the nav keeps its natural width first;
   only past the field's own minimum does the nav start to scroll. */
.search-slot {
  display: flex;
  flex: 1 1 0;
  justify-content: center;
  min-width: min-content;
  padding: 0 clamp(0rem, 4vw - 1rem, 3rem);
}

.search-button {
  display: inline-flex;
  flex: 1;
  align-items: center;
  gap: 0.45rem;
  max-width: 30rem;
  padding: 0.3rem 0.4rem 0.3rem 0.6rem;
  font: inherit;
  font-size: 13px;
  color: var(--muted);
  background: var(--bg);
  border: 1px solid var(--line);
  border-radius: var(--radius-sm);
  cursor: pointer;
}

.search-button:hover {
  color: var(--fg-2);
  border-color: var(--accent-line);
}

.search-label {
  flex: 1;
  min-width: 6rem;
  text-align: left;
}

/* After the search slot, which takes the free space. */
.search-slot + .identity {
  margin-left: 0;
}

.identity {
  margin-left: auto;
  flex-shrink: 0;
  display: flex;
  gap: 0.5rem;
  align-items: center;
  font-size: 13px;
  color: var(--fg-2);
}

.avatar {
  display: inline-grid;
  place-items: center;
  width: 26px;
  height: 26px;
  font-size: 11px;
  font-weight: 650;
  color: var(--accent);
  background: var(--accent-soft);
  border-radius: 50%;
}

.identity-error {
  color: var(--warn);
}

.shell-main {
  flex: 1;
  min-height: 0;
  overflow: auto;
}

.offline {
  flex-shrink: 0;
  margin: 0;
  padding: 0.35rem 1rem;
  font-size: 12.5px;
  color: var(--warn);
  background: var(--surface-2);
  border-bottom: 1px solid var(--line);
}

.banner {
  margin: 1rem;
  padding: 0.75rem 1rem;
  border: 1px solid var(--warn);
  border-radius: var(--radius-sm);
  color: var(--warn);
}

/*
 * Installed in Chrome on a desktop (the manifest's display_override): the
 * header is the title bar, so the window shows one band instead of a blank
 * title bar above the menu. titlebar-area-* is the part the window controls
 * leave free; the header can drag the window except where it is a control.
 */
@media (display-mode: window-controls-overlay) {
  .shell-header {
    padding-left: calc(env(titlebar-area-x, 0px) + 0.75rem);
    padding-right: calc(
      100vw - env(titlebar-area-x, 0px) - env(titlebar-area-width, 100vw) + 0.5rem
    );
    -webkit-app-region: drag;
    app-region: drag;
  }

  .shell-header a,
  .shell-header button {
    -webkit-app-region: no-drag;
    app-region: no-drag;
  }

  .nav a {
    padding: 0.2rem 0.6rem;
  }
}

@media (max-width: 600px) {
  .shell-header {
    gap: 0.5rem;
    padding: 0 0.75rem;
  }

  .nav a {
    padding: 0.3rem 0.35rem;
  }

  /* Just the icon, against the identity. */
  .search-slot {
    justify-content: flex-end;
    padding: 0;
  }

  .search-button {
    flex: none;
    padding: 0.35rem;
    border-color: transparent;
    background: none;
  }

  /* Search and Sign out shrink to their icons. */
  .search-label,
  .search-kbd,
  .login,
  .brand-name,
  .sign-out-label {
    display: none;
  }
}

@media (min-width: 601px) {
  .sign-out svg {
    display: none;
  }
}
</style>
